'use client';

import { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Container,
  Paper,
  Grid,
  Chip,
  Stepper,
  Step,
  StepLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  CheckCircle,
  WarningAmber,
  ArrowForward,
  ExpandMore,
  FileDownload,
  NotificationsActive,
  TrackChanges,
  FactCheck,
  Assignment,
  Gavel,
  AccountBalance,
  DocumentScanner,
  FolderSpecial,
  Rule,
  Dashboard,
  Flag,
} from '@mui/icons-material';
import Header from '../../components/HomepageContent/Header';
import Footer from '../../components/HomepageContent/Footer';
import Link from 'next/link';
import '../features-responsive.css';

const BRAND_BLUE = '#0F4C9C';
const BRAND_GREEN = '#63A821';

const workflowSteps = [
  'Alert',
  'Track Deficiencies',
  'Respond',
  'Dispute & Appeal',
  'Close',
];

const painPoints = [
  'Leadership learns surveyors are in the building hours—or days—after entrance',
  '2567 deficiencies, F-tags, and scope & severity tracked in spreadsheets that drift out of date',
  'Plan of correction deadlines managed by memory, with real CMP dollars on the line',
  'IDR and appeal windows missed because nothing tracks the stages',
  'Federal remedies, abatement letters, and correspondence scattered across inboxes',
  'No portfolio view of open surveys, outstanding remedies, and compliance status across facilities',
];

const capabilities = [
  {
    icon: NotificationsActive,
    title: 'Survey Alerts',
    description: 'Log a survey the moment it begins—state, facility, site location, notification type, survey type, agency, entrance and exit date-times, and the agency personnel involved—so leadership and corporate know immediately, not at the exit conference.',
  },
  {
    icon: TrackChanges,
    title: 'Full Survey Lifecycle Tracking',
    description: 'Every survey carries dedicated tracking for each stage—survey detail, 2567, plan of correction, IDR, revised 2567 and POC, CMS decision, and federal monitoring—with color-coded status indicators so you can see exactly where each survey stands at a glance.',
  },
  {
    icon: FactCheck,
    title: '2567 Deficiency & F-Tag Tracking',
    description: 'Record every deficiency with its F-tag or K-tag, scope & severity, and substandard quality of care flag. Track disputed deficiencies, state decisions, and IDR outcomes at the tag level—so your deficiency history is structured data, not a PDF in a folder.',
  },
  {
    icon: Assignment,
    title: 'Plan of Correction Management',
    description: 'Manage the POC for every deficiency—level of harm, residents affected, and the correction plan—through submission and acceptance, with revised POC cycles tracked when the first response isn\'t the last.',
  },
  {
    icon: Gavel,
    title: 'IDR & Dispute Tracking',
    description: 'Track informal dispute resolution from submission through outcome, including disputed deficiencies, state decisions, and the revised 2567/POC documents that follow.',
  },
  {
    icon: AccountBalance,
    title: 'Federal Remedies & Abatement',
    description: 'Track federal remedies—including CMP types and remedy status—plus abatement letters and compliance dates, so the financial side of enforcement is managed with the same rigor as the clinical side.',
  },
  {
    icon: DocumentScanner,
    title: 'Survey Document Intelligence',
    description: 'Upload CMS 2567 and POC documents and let CareAscendium extract the deficiency details—F-tags, statements of deficiency, level of harm, residents affected, and correction plans—into structured records instead of manual re-keying.',
  },
  {
    icon: FolderSpecial,
    title: 'Documents & Notes',
    description: 'Attach survey documents and correspondence to the survey record, and keep private and follow-up notes by note type—one complete file per survey.',
  },
  {
    icon: Rule,
    title: 'Survey Types for Every Scenario',
    description: 'Track annual surveys, complaint surveys, revisits, desk audits, federal lookbacks, and more—each with compliance status, complaint types, and associated child surveys linked to the parent.',
  },
  {
    icon: Dashboard,
    title: 'Portfolio Survey Dashboards',
    description: 'The survey management dashboard gives operators portfolio-wide reporting—open survey activity and leaseholder reports across facilities—with Excel export and configurable columns for leadership review.',
  },
  {
    icon: Flag,
    title: 'Special Focus Facility Awareness',
    description: 'Flag special focus facilities and track outstanding remedies, so the facilities with the most regulatory exposure get the most attention.',
  },
];

const roles = [
  {
    title: 'Administrators',
    benefit: 'Instant survey alerts and one live record of every stage, date, and deadline',
  },
  {
    title: 'Compliance & regulatory leaders',
    benefit: 'Structured 2567, POC, IDR, and remedy tracking instead of spreadsheet archaeology',
  },
  {
    title: 'Corporate / multi-site operators',
    benefit: 'Portfolio dashboards showing open surveys, statuses, and exposure across every facility',
  },
  {
    title: 'Quality & QAPI leaders',
    benefit: 'Deficiency and F-tag history as structured data to focus improvement work',
  },
  {
    title: 'Legal & finance teams',
    benefit: 'CMP, remedy, and abatement tracking with the supporting documents attached',
  },
];

const outcomes = [
  { value: 'Instant', label: 'Time to awareness', detail: 'Survey alerts logged at entrance, visible to leadership immediately' },
  { value: 'Clear', label: 'Stage visibility', detail: 'Color-coded status across 2567, POC, IDR, CMS, and federal monitoring' },
  { value: 'Data', label: 'Deficiency history', detail: 'Structured F-tag records with scope & severity across every survey' },
  { value: 'Met', label: 'Deadline control', detail: 'POC, IDR, and abatement stages tracked instead of remembered' },
  { value: '100%', label: 'Portfolio exposure', detail: 'Open surveys and outstanding remedies across all facilities in one dashboard' },
  { value: 'All', label: 'Document completeness', detail: 'Every 2567, POC, letter, and note attached to the survey record' },
];

const integrations = [
  'Critical Element Pathways',
  'Mock Surveys',
  'QAPI',
  'Document Vault',
  'Multi-Facility Management',
];

const complianceItems = [
  'A complete, dated record of every survey from entrance to closure',
  'Deficiency-level tracking with F-tags, scope & severity, and SQC flags',
  'Plan of correction and revised POC cycles documented per deficiency',
  'IDR submissions and outcomes tracked at the tag level',
  'Federal remedies, CMP types, and abatement letters managed with dates',
  'Portfolio-level reporting for governance and corporate oversight',
];

const faqs = [
  {
    q: 'What happens when surveyors arrive?',
    a: 'Log a survey alert with the facility, survey type, agency, and entrance date-time. The alert creates the survey record that carries the rest of the lifecycle, and leadership sees it immediately.',
  },
  {
    q: 'Can it track the 2567 at the deficiency level?',
    a: 'Yes. Each deficiency is tracked with its F-tag or K-tag, scope & severity, substandard quality of care flag, dispute status, state decision, and IDR outcome.',
  },
  {
    q: 'Does it manage plans of correction?',
    a: 'Yes. POC details—level of harm, residents affected, and the correction plan—are tracked per deficiency, including revised 2567 and POC cycles when responses are amended.',
  },
  {
    q: 'Can we avoid re-typing the 2567 into the system?',
    a: 'Yes. Upload the 2567 or POC document and CareAscendium extracts the deficiency details into structured records.',
  },
  {
    q: 'What about IDR and federal remedies?',
    a: 'IDR is tracked from submission through outcome, and federal remedies—including CMP types, remedy status, and abatement letters—are managed on the same survey record.',
  },
  {
    q: 'Can corporate see surveys across all facilities?',
    a: 'Yes. The survey management dashboard provides portfolio-wide open survey activity and leaseholder reporting with Excel export—one view of survey exposure across the organization.',
  },
  {
    q: 'What survey types are supported?',
    a: 'Annual surveys, complaint surveys (including combined variants), revisits, desk audits, federal lookbacks, and more—with associated child surveys linked to the parent record.',
  },
];

export default function SurveyManagementClient() {
  const [expanded, setExpanded] = useState<number | false>(0);

  const handleChange = (panel: number) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  useEffect(() => {
    document.title = 'Survey Management Software for Skilled Nursing Facilities | CareAscendium';
  }, []);

  return (
    <Box className="feature-page-wrapper" sx={{ minHeight: '100vh', bgcolor: '#fafafa', display: 'flex', flexDirection: 'column' }}>
      <Header />

      {/* Hero */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${BRAND_BLUE} 0%, #1E4A8C 50%, #2A5A9C 100%)`,
          py: { xs: 6, md: 8 },
          px: 4,
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Chip
            label="Survey Management & Insights"
            sx={{
              bgcolor: 'rgba(255,255,255,0.15)',
              color: 'white',
              fontWeight: 600,
              mb: 3,
              border: '1px solid rgba(255,255,255,0.3)',
            }}
          />
          <Typography
            variant="h1"
            sx={{
              color: 'white',
              mb: 3,
              fontWeight: 800,
              fontSize: { xs: '2rem', md: '3.25rem' },
              lineHeight: 1.15,
              mx: 'auto',
              maxWidth: 1000,
            }}
          >
            Manage Every Survey—From the Knock on the Door Through 2567, POC, IDR, and Closure
          </Typography>
          <Typography
            variant="h5"
            sx={{
              color: 'rgba(255,255,255,0.92)',
              mx: 'auto',
              lineHeight: 1.6,
              fontWeight: 300,
              fontSize: { xs: '1.05rem', md: '1.35rem' },
              mb: 5,
              maxWidth: 900,
            }}
          >
            CareAscendium gives skilled nursing operators one system to track the complete survey lifecycle—entrance alerts, deficiency and F-tag tracking with scope & severity, plans of correction, dispute resolution, federal remedies, and portfolio-wide survey dashboards.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link href="/contact" style={{ textDecoration: 'none' }}>
              <Button
                variant="contained"
                size="large"
                endIcon={<ArrowForward />}
                sx={{
                  bgcolor: BRAND_GREEN,
                  color: 'white',
                  px: 4,
                  py: 1.5,
                  fontWeight: 700,
                  borderRadius: '6px',
                  textTransform: 'none',
                  '&:hover': { bgcolor: '#55901d' },
                }}
              >
                REQUEST A DEMO
              </Button>
            </Link>

          </Box>
        </Container>
      </Box>

      {/* Problem */}
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
        <Grid container spacing={5} alignItems="center" justifyContent="center">
          <Grid item xs={12} md={10} sx={{ textAlign: 'center' }}>
            <Typography variant="h2" sx={{ color: BRAND_BLUE, mb: 3, fontWeight: 700, fontSize: { xs: '1.75rem', md: '2.25rem' } }}>
              The Challenge with Spreadsheet Survey Tracking
            </Typography>
            <Typography sx={{ color: '#666', mb: 4, lineHeight: 1.7, fontSize: '1.05rem', maxWidth: 850, mx: 'auto' }}>
              When surveyors walk in, the whole organization needs to know—and from that moment, every date, deficiency, remedy, and response carries regulatory and financial consequences. Most operators track this in spreadsheets and email chains: leadership finds out late, POC deadlines sneak up, IDR windows close, and nobody can see survey exposure across the portfolio.
            </Typography>
            <Box component="ul" sx={{ pl: 0, listStyle: 'none', m: 0, display: 'inline-block', textAlign: 'left' }}>
              {painPoints.map((point) => (
                <Box
                  component="li"
                  key={point}
                  sx={{ display: 'flex', alignItems: 'flex-start', mb: 1.5, gap: 1.5 }}
                >
                  <WarningAmber sx={{ color: '#FF9800', mt: 0.2, flexShrink: 0 }} />
                  <Typography sx={{ color: '#555', lineHeight: 1.5 }}>{point}</Typography>
                </Box>
              ))}
            </Box>
          </Grid>
          <Grid item xs={12} md={10}>
            <Paper
              elevation={4}
              sx={{
                p: 4,
                borderRadius: 3,
                background: 'linear-gradient(145deg, #F3F1FF 0%, #ffffff 100%)',
                borderLeft: `4px solid ${BRAND_BLUE}`,
              }}
            >
              <Typography
                sx={{
                  fontStyle: 'italic',
                  color: BRAND_BLUE,
                  fontSize: '1.15rem',
                  lineHeight: 1.6,
                  fontWeight: 500,
                }}
              >
                &ldquo;Every day between the 2567 and an accepted plan of correction has a cost. You can't manage what you're tracking in a spreadsheet.&rdquo;
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* Solution / Workflow */}
      <Box sx={{ bgcolor: '#f0f4fa', py: { xs: 4, md: 6 } }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h2" sx={{ color: BRAND_BLUE, mb: 2, fontWeight: 700 }}>
              One Record. From Entrance to Closure.
            </Typography>
            <Typography sx={{ color: '#666', maxWidth: 780, mx: 'auto', lineHeight: 1.7, fontSize: '1.05rem' }}>
              CareAscendium Survey Management tracks the entire survey lifecycle in one place. A survey alert captures the entrance the moment it happens; the survey record then carries every stage—2567 deficiencies, plan of correction, IDR, revised 2567 and POC, CMS decisions, and federal monitoring—with color-coded status at every step and documents and notes attached where they belong.
            </Typography>
          </Box>
          <Paper elevation={2} sx={{ p: { xs: 3, md: 4 }, borderRadius: 3, bgcolor: 'white' }}>
            <Stepper
              alternativeLabel
              activeStep={-1}
              sx={{
                '& .MuiStepLabel-label': { fontWeight: 600, color: BRAND_BLUE, mt: 1 },
                '& .MuiStepIcon-root': { color: BRAND_BLUE, '&.Mui-completed': { color: BRAND_GREEN } },
              }}
            >
              {workflowSteps.map((label) => (
                <Step key={label} completed>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(5, 1fr)' }, mt: 4, gap: 2, textAlign: 'center' }}>
               <Typography sx={{ color: '#666', fontSize: '0.9rem', px: 1 }}>
                 <b>Alert:</b> Log the survey the moment surveyors arrive
               </Typography>
               <Typography sx={{ color: '#666', fontSize: '0.9rem', px: 1 }}>
                 <b>Track Deficiencies:</b> Record findings with F-tags and scope & severity
               </Typography>
               <Typography sx={{ color: '#666', fontSize: '0.9rem', px: 1 }}>
                 <b>Respond:</b> Manage POC through submission and acceptance
               </Typography>
               <Typography sx={{ color: '#666', fontSize: '0.9rem', px: 1 }}>
                 <b>Dispute & Appeal:</b> Track IDR outcomes and revised POC cycles
               </Typography>
               <Typography sx={{ color: '#666', fontSize: '0.9rem', px: 1 }}>
                 <b>Close:</b> Manage federal remedies and CMS decisions
               </Typography>
            </Box>
          </Paper>
        </Container>
      </Box>

      {/* Capabilities */}
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h2" sx={{ color: BRAND_BLUE, mb: 2, fontWeight: 700 }}>
            Key Capabilities
          </Typography>
        </Box>
        <Box 
          sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' }, 
            gap: 3,
            alignItems: 'stretch'
          }}
        >
          {capabilities.map((cap) => (
            <Paper
              key={cap.title}
              elevation={3}
              sx={{
                p: 3,
                height: '100%',
                borderRadius: 3,
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 12px 28px rgba(21,60,127,0.12)',
                },
              }}
            >
              <Box
                sx={{
                  width: 52,
                  height: 52,
                  borderRadius: '50%',
                  bgcolor: `${BRAND_BLUE}15`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 2,
                }}
              >
                <cap.icon sx={{ color: BRAND_BLUE, fontSize: 26 }} />
              </Box>
              <Typography variant="h6" sx={{ color: BRAND_BLUE, fontWeight: 700, mb: 1.5 }}>
                {cap.title}
              </Typography>
              <Typography sx={{ color: '#666', mb: 2, lineHeight: 1.6, fontSize: '0.95rem' }}>
                {cap.description}
              </Typography>
            </Paper>
          ))}
        </Box>
      </Container>

      {/* Roles + Integration */}
      <Box sx={{ bgcolor: '#f0f4fa', py: { xs: 4, md: 6 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={5} justifyContent="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h2" sx={{ color: BRAND_BLUE, mb: 3, fontWeight: 700, fontSize: '1.75rem' }}>
                Built for Every Role
              </Typography>
              {roles.map((role) => (
                <Box key={role.title} sx={{ display: 'flex', gap: 1.5, mb: 2.5 }}>
                  <CheckCircle sx={{ color: BRAND_GREEN, mt: 0.2, flexShrink: 0 }} />
                  <Box>
                    <Typography sx={{ fontWeight: 700, color: BRAND_BLUE }}>{role.title}</Typography>
                    <Typography sx={{ color: '#666', fontSize: '0.95rem', lineHeight: 1.5 }}>
                      {role.benefit}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper
                elevation={3}
                sx={{
                  p: 4,
                  borderRadius: 3,
                  height: '100%',
                  background: `linear-gradient(145deg, ${BRAND_BLUE} 0%, #1E4A8C 100%)`,
                  color: 'white',
                }}
              >
                <Typography variant="h3" sx={{ fontWeight: 700, mb: 2, fontSize: '1.5rem' }}>
                  Part of the CareAscendium Platform
                </Typography>
                <Typography sx={{ opacity: 0.9, mb: 3, lineHeight: 1.6 }}>
                  Survey Management connects the survey record to the readiness work around it:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                  {integrations.map((item) => (
                    <Chip
                      key={item}
                      label={item}
                      sx={{
                        bgcolor: 'rgba(255,255,255,0.15)',
                        color: 'white',
                        fontWeight: 600,
                        border: '1px solid rgba(255,255,255,0.25)',
                      }}
                    />
                  ))}
                </Box>
                <Typography sx={{ fontWeight: 600, color: '#a8d86a', fontStyle: 'italic' }}>
                  One platform. One source of truth. Every survey managed, not survived.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Outcomes & Compliance */}
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
        <Box sx={{ textAlign: 'center', mb: 5 }}>
          <Typography variant="h2" sx={{ color: BRAND_BLUE, mb: 2, fontWeight: 700 }}>
            Outcomes You Can Measure
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
            gap: 3,
            mb: 8,
            alignItems: 'stretch',
            maxWidth: 1000,
            mx: 'auto'
          }}
        >
          {outcomes.map((item) => (
            <Paper
              key={item.label}
              elevation={2}
              sx={{
                p: 3,
                textAlign: 'center',
                borderRadius: 3,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
              }}
            >
              <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: BRAND_GREEN, mb: 0.5 }}>
                {item.value}
              </Typography>
              <Typography sx={{ fontWeight: 700, color: BRAND_BLUE, mb: 0.5, fontSize: '0.95rem' }}>
                {item.label}
              </Typography>
              <Typography sx={{ color: '#888', fontSize: '0.85rem' }}>{item.detail}</Typography>
            </Paper>
          ))}
        </Box>

        <Box sx={{ textAlign: 'center', mb: 5 }}>
          <Typography variant="h2" sx={{ color: BRAND_BLUE, mb: 2, fontWeight: 700 }}>
            Documentation You Can Stand Behind
          </Typography>
          <Typography sx={{ color: '#666', maxWidth: 780, mx: 'auto', lineHeight: 1.7, fontSize: '1.05rem', mb: 4 }}>
            CareAscendium supports the regulatory discipline survey response demands:
          </Typography>
          <Box sx={{ textAlign: 'left', width: 'fit-content', mx: 'auto' }}>
            {complianceItems.map((item) => (
              <Box key={item} sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                <CheckCircle sx={{ color: BRAND_GREEN, mt: 0.5, flexShrink: 0 }} />
                <Typography sx={{ color: '#555', fontSize: '1.05rem', lineHeight: 1.5 }}>
                  {item}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Container>

      {/* FAQ */}
      <Box sx={{ py: { xs: 10, md: 14 }, px: 4, background: 'linear-gradient(113.99deg, #153C7F 12.55%, #000820 87.43%)' }}>
        <Container maxWidth="md">
          <Typography variant="h3" sx={{ fontWeight: 800, color: 'white', mb: 6, textAlign: 'center', fontSize: { xs: '1.75rem', md: '2.5rem' } }}>
            Frequently Asked Questions
          </Typography>
          <Box>
            {faqs.map((faq, i) => (
              <Accordion 
                key={i}
                expanded={expanded === i} 
                onChange={handleChange(i)}
                sx={{ 
                  mb: 2.5, 
                  boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '12px !important',
                  bgcolor: 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(10px)',
                  color: 'white',
                  overflow: 'hidden',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.08)'
                  },
                  '&:before': { display: 'none' }
                }}
              >
                <AccordionSummary expandIcon={<ExpandMore sx={{ color: 'white' }} />} sx={{ px: { xs: 2, md: 4 }, py: 1.5 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', letterSpacing: '0.02em' }}>{faq.q}</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: { xs: 2, md: 4 }, pb: 4 }}>
                  <Typography sx={{ color: 'rgba(255,255,255,0.85)', lineHeight: 1.8, fontSize: '1.05rem' }}>
                    {faq.a}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </Container>
      </Box>

      {/* CTA */}
      <Box sx={{ py: 6, bgcolor: '#f0f4fa' }}>
        <Container maxWidth="lg">
          <Box
            sx={{
              backgroundImage: `url('/assets/osha/bgdemo.svg'), linear-gradient(113.99deg, #0F4C9C 12.55%, #000820 87.43%)`,
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              backgroundSize: 'cover',
              borderRadius: '16px',
              minHeight: '300px',
              py: 5,
              px: 3,
              textAlign: 'center',
              color: 'white',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 15px 40px rgba(0, 8, 32, 0.12)',
            }}
          >
            <Typography variant="h2" sx={{ mb: 2, fontWeight: 800, fontSize: { xs: '1.75rem', md: '2.5rem' }, textTransform: 'uppercase' }}>
              Ready to Take Control of the Survey Cycle?
            </Typography>
            <Typography sx={{ mb: 4, opacity: 0.9, lineHeight: 1.6, fontSize: '1.1rem', maxWidth: 850 }}>
              See how CareAscendium helps SNF operators manage every survey stage, deadline, and deficiency—with one record from the knock on the door through closure.
            </Typography>
            <Link href="/contact" style={{ textDecoration: 'none' }}>
              <Button
                variant="contained"
                size="large"
                sx={{
                  bgcolor: BRAND_GREEN,
                  color: 'white',
                  px: 5,
                  py: 1.5,
                  fontWeight: 700,
                  borderRadius: '30px',
                  textTransform: 'none',
                  fontSize: '1.1rem',
                  '&:hover': { bgcolor: '#528a1b' },
                  boxShadow: '0 4px 10px rgba(99, 168, 33, 0.2)',
                }}
              >
                REQUEST A DEMO
              </Button>
            </Link>
          </Box>
        </Container>
      </Box>

      <Footer />
    </Box>
  );
}
